import { useTranslations } from 'next-intl';
import { AppInsightsContext, MetadataContext, TrackEventKey, TrackEventName, TrackEventType, UserContext } from '..';
import classNames from 'classnames';
import { ReportsSearchFilterType } from './SearchFilters';
import Paginator from './Paginator';
import ReportCard from '../report/ReportCard';
import { useContext, useEffect, useState } from 'react';
import { CognitiveSearchResultModel, ReportSearchModel } from '@/types';
import { useRouter } from 'next/router';
import RadioButton from '../formControl/RadioButton';
import SquaresFourLight from '@/assets/squaresFourLight.svg';
import ListLight from '@/assets/listLight.svg';
import { countFilters, replaceValuesOfRouteQuery } from '@/utils';
import { motion } from 'motion/react';
import { accordionPanel } from '@/animations';
import SearchFilterOptions from './SearchFilterOptions';
import ReportListCard from '../report/ReportListCard';
import tailwindConfig from '@/tailwind.screens';
import SearchNoResult from './SearchNoResult';

type ReportSearchResultModel = {
  availableFilters: ReportsSearchFilterType[];
  publicSiteSearch: boolean;
  result: CognitiveSearchResultModel<ReportSearchModel>;
  pageSize: number;
  loading?: boolean;
  orderBy: 'score' | 'date';
  setOrderBy: (orderBy: 'score' | 'date') => void;
};

const ReportSearchResult = ({
  result,
  pageSize,
  availableFilters,
  publicSiteSearch,
  orderBy,
  loading,
  setOrderBy,
}: ReportSearchResultModel) => {
  const t = useTranslations();
  const [listMode, setListMode] = useState<'grid' | 'list'>('grid');
  const [mobileFiltersPanelOpen, setMobileFiltersPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTabbed, setIsTabbed] = useState(false);
  const router = useRouter();
  const appInsights = useContext(AppInsightsContext);
  const { pageId } = useContext(MetadataContext);
  const user = useContext(UserContext);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storage = localStorage.getItem('reportSearchGridMode');
      if (storage) {
        setListMode(storage === 'grid' ? 'grid' : 'list');
      }
    }

    const onResize = () => {
      setIsMobile(window.innerWidth < parseInt(tailwindConfig.screens.sm, 10));
      setIsTabbed(window.innerWidth < parseInt(tailwindConfig.screens.md, 10));
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('reportSearchGridMode', listMode);
    }
  }, [listMode]);

  const onReportCardClick = (e: React.MouseEvent, data: ReportSearchModel) => {
    appInsights.trackCustomEvent(
      TrackEventName.SEARCH_PAGE,
      TrackEventKey.SEARCH_REPORT,
      TrackEventType.SELECTED,
      user,
      {
        pageId,
        pagePath: router.asPath,
        reportID: data.reportId,
        searchTerm: router.query,
      },
    );
  };

  const onClear = () => {
    replaceValuesOfRouteQuery(router, {});
  };

  const numberOfFilters = countFilters(router);

  return (
    <div className="container my-3">
      <div className="flex flex-col flex-nowrap gap-2 md:flex-row md:items-center md:justify-between">
        <div className="text-[22px] font-light lg:w-1/2 lg:text-[40px]">
          {t('search.$searchtotalresults', {
            totalResults: result?.totalResults || 0,
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-1 md:justify-start md:gap-10 lg:w-1/2">
          <div className="-ml-3 flex gap-1 md:ml-0 md:gap-2">
            <RadioButton
              id="score"
              name="orderBy"
              value="score"
              checked={orderBy === 'score'}
              onChange={() => setOrderBy('score')}
              label={
                <>
                  <span>Relevance</span>
                  <span className="hidden sm:inline">&nbsp;(Recommended)</span>
                </>
              }
            />
            <RadioButton
              id="date"
              name="orderBy"
              value="date"
              checked={orderBy === 'date'}
              onChange={() => setOrderBy('date')}
              label="Date"
            />
          </div>

          <div className="flex overflow-hidden rounded-full border border-black/20 bg-white md:hidden">
            <button
              onClick={() => setMobileFiltersPanelOpen(!mobileFiltersPanelOpen)}
              className="btn !px-3 whitespace-nowrap"
            >
              {t('general.$filters')}
              {numberOfFilters > 0 && ` (${numberOfFilters})`}
            </button>
            {numberOfFilters > 0 && (
              <button onClick={onClear} className="btn !rounded-none border-l border-black/20 !px-3 whitespace-nowrap">
                {t('search.$clearAll')}
              </button>
            )}
          </div>

          <div className="hidden overflow-hidden rounded-full border border-black/20 bg-white md:flex">
            <button
              className={classNames(
                listMode === 'grid' && 'bg-black/10',
                'cursor-pointer border-r border-black/20 px-4 py-2.5 transition-colors',
              )}
              onClick={() => setListMode('grid')}
            >
              <SquaresFourLight className="h-5 w-5" />
            </button>

            <button
              className={classNames(
                listMode === 'list' && 'bg-black/10',
                'cursor-pointer px-4 py-2.5 transition-colors',
              )}
              onClick={() => setListMode('list')}
            >
              <ListLight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isTabbed && (
          <motion.div
            variants={accordionPanel.variants}
            initial="inactive"
            animate={mobileFiltersPanelOpen ? 'active' : 'inactive'}
            className={classNames('print:!h-auto print:!opacity-100', !mobileFiltersPanelOpen && 'overflow-hidden')}
          >
            <SearchFilterOptions
              availableFilters={availableFilters}
              facets={result.facets}
              isPublic={publicSiteSearch}
            />
          </motion.div>
        )}
      </div>

      <div>
        <Paginator pageSize={pageSize} totalResults={result.totalResults || 0} queryMode className="lg:mt-3" />
      </div>
      <div className="-mx-3 mt-3 flex flex-wrap">
        {result.results.map((report) => (
          <div
            key={report.reportId}
            className={classNames(
              listMode === 'grid' ? 'xxl:w-3/12 w-full p-3 md:w-1/2 lg:w-4/12' : 'flex w-full flex-col',
            )}
          >
            {listMode === 'grid' && !isMobile ? (
              <ReportCard key={report.reportId} data={report} onClick={onReportCardClick} />
            ) : (
              <ReportListCard
                key={report.reportId}
                data={report}
                onClick={onReportCardClick}
                small={isMobile}
                showActionButtons
              />
            )}
          </div>
        ))}
      </div>
      <div>
        <Paginator
          pageSize={pageSize}
          goToElement="#listing-grid"
          totalResults={result.totalResults || 0}
          queryMode
          className="lg:mt-3"
        />
      </div>
      {result.totalResults === 0 && !loading && (
        <SearchNoResult
          searchTerm={
            Array.isArray(router.query.searchTerm) ? router.query.searchTerm[0] : router.query.searchTerm || ''
          }
          spellCheck={result.spellCheck}
        />
      )}
    </div>
  );
};

export default ReportSearchResult;
